import { useLayoutEffect, useRef, useState } from 'react';

// Telefon-keret a Mobil nézethez (Tervező design-minták, Design sablonok): márkátlan, CSS-rajzolt test, benne a design a választott
// csúcstelefon kijelző-méretén CSS-pixelben — ekkora ablakot lát a weboldal valóban (a fizikai felbontás ennek DPR-szorosa).
// A keret a panelhez kicsinyedik (a százalék látszik) vagy 1:1-ben görgethető. Hajtogathatók szándékosan nincsenek a listában.
export interface Phone { id: string; brand: 'Samsung' | 'iPhone'; name: string; w: number; h: number; dpr: number; inch: string }
export const PHONES: Phone[] = [
  { id: 's-ultra', brand: 'Samsung', name: 'Galaxy S26 Ultra / S25 Ultra / S24 Ultra', w: 384, h: 832, dpr: 3.75, inch: '6.8–6.9″' },
  { id: 's-plus', brand: 'Samsung', name: 'Galaxy S26+ / S25+ / S24+', w: 384, h: 832, dpr: 3.75, inch: '6.7″' },
  { id: 's-base', brand: 'Samsung', name: 'Galaxy S26 / S25 / S24', w: 360, h: 780, dpr: 3, inch: '6.2–6.3″' },
  { id: 's23-ultra', brand: 'Samsung', name: 'Galaxy S23 Ultra / S22 Ultra', w: 384, h: 824, dpr: 3.75, inch: '6.8″' },
  { id: 'i-promax', brand: 'iPhone', name: 'iPhone 17 Pro Max / 16 Pro Max', w: 440, h: 956, dpr: 3, inch: '6.9″' },
  { id: 'i-pro', brand: 'iPhone', name: 'iPhone 17 / 17 Pro / 16 Pro', w: 402, h: 874, dpr: 3, inch: '6.3″' },
  { id: 'i-air', brand: 'iPhone', name: 'iPhone Air', w: 420, h: 912, dpr: 3, inch: '6.5″' },
  { id: 'i-16', brand: 'iPhone', name: 'iPhone 16 / 15 / 14 Pro', w: 393, h: 852, dpr: 3, inch: '6.1″' },
  { id: 'i-plus', brand: 'iPhone', name: 'iPhone 16 Plus / 15 Plus / 15 Pro Max', w: 430, h: 932, dpr: 3, inch: '6.7″' },
];
const BRANDS: Phone['brand'][] = ['Samsung', 'iPhone'];
const KEY = 'n4.phone'; const KEY_LAND = 'n4.phone.land';
const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* privát mód: nem baj, csak nem marad meg */ } };

// A választott telefon és az álló/fekvő állapot; mindkettő megmarad a böngészőben (minden design és sablon ugyanazt kapja).
export function usePhone() {
  const [id, setIdState] = useState(() => read(KEY) || PHONES[0]!.id);
  const [land, setLandState] = useState(() => read(KEY_LAND) === '1');
  const phone = PHONES.find((p) => p.id === id) ?? PHONES[0]!;
  const setId = (v: string) => { setIdState(v); write(KEY, v); };
  const setLand = (v: boolean) => { setLandState(v); write(KEY_LAND, v ? '1' : '0'); };
  return { id: phone.id, phone, land, setId, setLand };
}
export type PhoneState = ReturnType<typeof usePhone>;

// Telefon-választó az eszköztárba: lenyíló (márkánként csoportosítva, a CSS-px mérettel) + elforgatás.
export function PhonePicker({ id, land, setId, setLand }: PhoneState) {
  return (
    <span className="a-phonepick">
      <select className="a-input" value={id} onChange={(e) => setId(e.target.value)} title="melyik telefon kijelzőjén nézed — CSS-pixelben: ekkora ablakot lát a weboldal">
        {BRANDS.map((b) => <optgroup key={b} label={b}>{PHONES.filter((p) => p.brand === b).map((p) => <option key={p.id} value={p.id}>{p.name} · {p.w}×{p.h}</option>)}</optgroup>)}
      </select>
      <button className={`a-btn sm${land ? ' pri' : ''}`} title="elforgatás: álló ↔ fekvő" onClick={() => setLand(!land)}>⟳ {land ? 'fekvő' : 'álló'}</button>
    </span>
  );
}

const BEZEL = 14; const MARGIN = 8; // a ház kávája a kijelző körül · hely a kilógó gomboknak és az árnyéknak
// A keret: a kijelző iframe-je pontosan a telefon CSS-px méretén áll; az egészet transform-mal kicsinyítjük a panelhez (a tartalom
// így a valódi szélességen tördelődik, csak a képe kisebb). 1:1-ben a terület görgethető.
export function PhoneFrame({ phone, land, title, src, srcDoc, sandbox, defaultFit = true }: { phone: Phone; land: boolean; title: string; src?: string; srcDoc?: string; sandbox?: string; defaultFit?: boolean }) {
  const sw = land ? phone.h : phone.w; const sh = land ? phone.w : phone.h;
  const boxW = sw + 2 * BEZEL + 2 * MARGIN; const boxH = sh + 2 * BEZEL + 2 * MARGIN;
  const area = useRef<HTMLDivElement>(null); const cap = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(defaultFit); // illesztve a panelhez, vagy 1:1 (az önálló lapon az utóbbi az alap)
  const [avail, setAvail] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = area.current; if (!el) return;
    const measure = () => {
      const cs = getComputedStyle(el); const capH = cap.current?.offsetHeight ?? 0;
      setAvail({ w: el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight), h: el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom) - capH - 8 });
    };
    measure(); const ro = new ResizeObserver(measure); ro.observe(el); return () => ro.disconnect();
  }, []);
  // Illesztés: szélességre mindig; magasságra csak ha a terület tényleg korlátos (mobilon a lap görget, ott csak a szélesség számít).
  const scale = fit && avail ? Math.max(0.1, Math.min(1, avail.w / boxW, avail.h > 240 ? avail.h / boxH : 1)) : 1;
  const pct = Math.round(scale * 100);
  return (
    <div ref={area} className="a-phone-area">
      <div ref={cap} className="a-phone-cap">
        <b>{phone.name}</b>
        <span title={`a kijelző CSS-pixelben — ekkora ablakot lát a weboldal (fizikai: ${Math.round(sw * phone.dpr)}×${Math.round(sh * phone.dpr)}, ${phone.inch})`}>{sw}×{sh} px{land ? ' · fekvő' : ''}</span>
        <button className={fit ? '' : 'on'} title={fit ? 'a panelhez illesztve — kattints az 1:1 mérethez' : '1:1 méret — kattints a panelhez illesztéshez'} onClick={() => setFit(!fit)}>{fit ? `${pct}%` : '1:1'}</button>
      </div>
      <div className="a-phone-fit" style={{ width: Math.round(boxW * scale), height: Math.round(boxH * scale), visibility: avail ? 'visible' : 'hidden' }}>
        <div className="a-phone-box" style={{ width: boxW, height: boxH, transform: `scale(${scale})` }}>
          <div className={`a-phone${land ? ' land' : ''}`}>
            <i className="key pw" /><i className="key v1" /><i className="key v2" />
            <div className="scr">
              <iframe title={title} src={src} srcDoc={srcDoc} sandbox={sandbox} style={{ width: sw, height: sh }} />
              <i className="cam" /><i className="home" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
